/* Since 3 November 2026 (packages/core EVERYTHING_FREE) this page says everything is free and lists it, with the
   passes a student may have bought before; the selling below shows only if selling comes back.
   /account/plans - the plans and prices for the student's price group, checkout and the passes they hold. Before
   buying, every student ticks the box: the pass starts as soon as they pay, and all sales are final (since 1 November
   2026). "Withdraw from contract here" (the 14-day refund until then) shows only while the server says a pass can still
   be withdrawn, which it no longer does. Stripe returns here with ?paid=1&session_id=… or ?canceled=1. */
import { useEffect, useRef, useState } from 'react';
import { SELLING, needsStartNow, planById, WEB_ROUTES, type CheckoutResponse, type Pass, type PlanView, type PlansResponse, type ProfileBundle } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link, replaceUrl, useLocation } from '../app/nav';
import { useMe, useSession } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Icon } from '../app/icons';
import { AiTag, Alert, Button, Checkbox, Chip, LoadError, Loading, PageHeader, Section, cx, type Tone } from '../app/ui';
import { api, ApiError } from '../lib/api';
import { formatDate, formatMoney, paymentPageUrl } from '../lib/format';
import { monthsText } from '../lib/i18n';
import { canWithdraw, coverUntil, covered, freshPass, passState, type PassState } from '../lib/passes';
import { isAiFeature } from '../content/legal';

type Paid = 'apply' | 'arrive' | 'bundle';   // 'bundle' (Apply & Arrive) is the one sold; the others are older passes
const STATE_TONE: Record<PassState, Tone> = { active: 'green', expired: 'gray', refunded: 'blue', canceled: 'gray' };
const POLL_MS = 2000, POLL_TRIES = 30;

export function Plans() {
  const { t, lang } = useT();
  const me = useMe();
  const { refresh } = useSession();
  const handle = useErrorHandler();
  const { query, pathname } = useLocation();
  const plans = useLoad(s => api.get<PlansResponse>(`/api/plans?lang=${lang}`, { signal: s }), [lang]);
  const profile = useLoad(s => api.get<ProfileBundle>('/api/profile', { signal: s }).catch(() => null), []);

  /* Stripe's return: remember it, then tidy the address bar */
  const [paid] = useState(() => query.get('paid') === '1');
  const [canceled] = useState(() => query.get('canceled') === '1');
  useEffect(() => { if (paid || canceled) replaceUrl(pathname); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const citizenship = profile.data?.profile.citizenship ?? [];
  const [serverWantsAck, setServerWantsAck] = useState(false);
  const askStartNow = serverWantsAck || needsStartNow([me.home_country, profile.data?.profile.home_country, ...citizenship]);
  const [startNow, setStartNow] = useState(false);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Paid | 'portal' | null>(null);
  const [message, setMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);
  const [withdrawn, setWithdrawn] = useState<Record<string, string>>({});

  const have = covered(me.plans.passes);

  const checkout = async (plan: Paid) => {
    if (busy) return;
    setMessage(null);
    if (askStartNow && !startNow) {
      setStartErr(t('v.tick'));
      document.getElementById('start-now')?.focus();
      return;
    }
    setBusy(plan);
    try {
      const r = await api.post<CheckoutResponse>('/api/checkout', { plan, start_now: askStartNow ? startNow : false });
      const url = paymentPageUrl(r.url);
      if (!url) throw new ApiError(500, 'server_error');
      setMessage({ tone: 'info', text: t('plans.redirecting') });
      window.location.assign(url);
    } catch (e) {
      setBusy(null);
      if (e instanceof ApiError && e.code === 'withdrawal_ack_required') {
        setServerWantsAck(true);
        setStartErr(t('err.withdrawal_ack_required'));
        requestAnimationFrame(() => document.getElementById('start-now')?.focus());
        return;
      }
      if (e instanceof ApiError && e.code === 'already_paid') { setMessage({ tone: 'info', text: t('err.already_paid') }); void refresh(); return; }
      const msg = handle(e);
      if (msg) setMessage({ tone: 'error', text: msg });
    }
  };

  const receipts = async () => {
    if (busy) return;
    setBusy('portal');
    try {
      const r = await api.post<{ url: string }>('/api/billing/portal');
      const url = paymentPageUrl(r.url);
      if (!url) throw new ApiError(500, 'server_error');
      window.location.assign(url);
    } catch (e) {
      setBusy(null);
      const msg = handle(e);
      if (msg) setMessage({ tone: 'error', text: msg });
    }
  };

  return (
    <>
      <PageHeader title={t('plans.title')} lead={SELLING ? t('plans.lead') : undefined} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      {paid && <PaidBanner />}
      {canceled && <div className="mb"><Alert tone="info">{t('plans.canceled')}</Alert></div>}
      {message && <div className="mb"><Alert tone={message.tone}>{message.text}</Alert></div>}

      {me.plans.passes.length > 0 && (
        <Section title={t('plans.yourPasses')} id="passes">
          <ul className="group list">
            {me.plans.passes.map(ps => (
              <li key={ps.id}>
                <PassRow pass={ps} withdrawnOn={withdrawn[ps.id]} onWithdrawn={date => setWithdrawn(w => ({ ...w, [ps.id]: date }))} />
              </li>
            ))}
          </ul>
          <div className="section__actions">
            <Button variant="secondary" small busy={busy === 'portal'} onClick={() => void receipts()}><Icon.pass />{t('plans.receipts')}</Button>
          </div>
        </Section>
      )}

      {/* since 3 November 2026 everything is free and nothing is sold: one card with everything */}
      {!SELLING ? (
        <Section title={t('plans.freeTitle')} id="choose">
          <p className="mb">{t('plans.freeLead')}</p>
          {plans.status === 'loading' ? <Loading /> : plans.status === 'error' ? <LoadError message={plans.message} retry={plans.reload} /> : (
            <div className="plans">
              {plans.data.plans.map(pl => (
                <PlanCard key={pl.id} plan={pl} busy={false} have={have} until={null} hasAnyPass onBuy={() => undefined} />
              ))}
            </div>
          )}
          <p className="fine">{t('land.price.abroad')}</p>
          <p className="fine">{t('plans.noFees')}</p>
        </Section>
      ) : (
      <Section title={t('plans.choose')} id="choose">
        {askStartNow && (
          <div className="card startnow">
            <h3 className="card__title">{t('plans.startNowTitle')}</h3>
            <Checkbox id="start-now" checked={startNow} onChange={v => { setStartNow(v); if (v) setStartErr(null); }}
              label={t('plans.startNow')} description={t('plans.startNowNote')} error={startErr} required />
          </div>
        )}
        {me.age === 'minor' && (
          <div className="mb askparent">
            <Alert tone="info">
              <p>{t('plans.askParentMinor')}</p>
              <p className="mt-sm"><Link to={WEB_ROUTES.family + '#fam-pay'} className="btn btn--secondary btn--small"><Icon.people />{t('plans.askParent')}</Link></p>
            </Alert>
          </div>
        )}
        {plans.status === 'loading' ? <Loading /> : plans.status === 'error' ? <LoadError message={plans.message} retry={plans.reload} /> : (
          <>
            <div className="plans">
              {plans.data.plans.map(pl => (
                <PlanCard key={pl.id} plan={pl} busy={busy === pl.id} have={have} minor={me.age === 'minor'}
                  until={pl.id === 'free' ? null : coverUntil(me.plans.passes, pl.id)}
                  hasAnyPass={me.plans.passes.some(p => passState(p) === 'active')} onBuy={() => void checkout(pl.id as Paid)} />
              ))}
            </div>
            <p className="refund"><Icon.info />{t('plans.refund')}</p>
            <p className="fine">{t('plans.prices', { tier: plans.data.tier })}</p>
            <p className="fine">{t('plans.noFees')}</p>
          </>
        )}
      </Section>
      )}
    </>
  );

}

/* after Stripe: wait for the webhook to add the pass (GET /api/me every 2 seconds, up to a minute) */
function PaidBanner() {
  const { t, lang } = useT();
  const { refresh } = useSession();
  const [since] = useState(() => Date.now());
  const [state, setState] = useState<'waiting' | 'ready' | 'slow'>('waiting');
  const [plan, setPlan] = useState<string | null>(null);
  useEffect(() => {
    let tries = 0, timer = 0, stop = false;
    const tick = async () => {
      tries++;
      const m = await refresh();
      if (stop) return;
      const fresh = m ? freshPass(m.plans.passes, since) : null;
      if (fresh) { setPlan(planById(fresh.plan).name[lang]); setState('ready'); return; }
      if (tries >= POLL_TRIES) { setState('slow'); return; }
      timer = window.setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => { stop = true; window.clearTimeout(timer); };
  }, [since]);
  return (
    <div className="mb">
      <Alert tone={state === 'slow' ? 'warn' : 'success'} title={t('plans.paidTitle')}>
        <p>{state === 'ready' ? t('plans.paidReady', { plan: plan ?? '' }) : state === 'slow' ? t('plans.paidSlow') : t('plans.paidWaiting')}</p>
        <p>{t('plans.paidEmail')}</p>
      </Alert>
    </div>
  );
}

function PlanCard({ plan, busy, have, until, hasAnyPass, onBuy, minor }: {
  plan: PlanView; busy: boolean; have: { apply: boolean; arrive: boolean }; until: string | null; hasAnyPass: boolean; onBuy: () => void; minor?: boolean;
}) {
  const { t, lang } = useT();
  const free = plan.id === 'free';
  const owned = plan.id === 'apply' ? have.apply : plan.id === 'arrive' ? have.arrive : plan.id === 'bundle' ? have.apply && have.arrive : false;
  const partial = plan.id === 'bundle' && !owned && (have.apply || have.arrive);
  const name = plan.name || planById(plan.id).name[lang];
  return (
    <article className={cx('plan', owned && 'plan--owned', free && 'plan--free')} aria-labelledby={`plan-${plan.id}`}>
      <div className="plan__head">
        <h3 className="plan__name" id={`plan-${plan.id}`}>{name}</h3>
        <p className="plan__price" dir="ltr">{formatMoney(plan.price_usd, lang)}</p>
      </div>
      {free ? (!hasAnyPass && <p className="plan__note">{t('plans.free')}</p>) : (
        <p className="plan__note">{t('plans.oneTime')} {t('plans.validFor', { months: monthsText(plan.valid_months, lang) })}</p>
      )}
      <ul className="includes">
        {plan.includes.map((item, i) => (
          <li key={i}><Icon.check /><span>{item}{isAiFeature(item) && <> <AiTag /></>}</span></li>
        ))}
      </ul>
      {!free && (
        <div className="plan__action">
          {owned ? <Chip tone="green">{until ? t('plans.owned', { date: formatDate(until, lang) }) : t('plans.ownedNoDate')}</Chip>
            : <>
                {/* a pass bought before Apply and Arrive were joined: Apply & Arrive adds the rest */}
                {partial && <p className="plan__note">{t('plans.bundlePartial', { plan: planById(have.apply ? 'apply' : 'arrive').name[lang] })}</p>}
                <Button busy={busy} onClick={onBuy}>{t('plans.get', { plan: name })}</Button>
                {/* someone else can pay: the Family page makes a payment link for this plan */}
                <p className={cx('plan__ask', minor && 'plan__ask--minor')}>
                  <Link to={`${WEB_ROUTES.family}?pay=${plan.id}`} className={minor ? 'btn btn--secondary btn--block' : undefined}>{t('plans.askParent')}</Link>
                </p>
              </>}
        </div>
      )}
    </article>
  );
}

function PassRow({ pass, withdrawnOn, onWithdrawn }: { pass: Pass; withdrawnOn?: string; onWithdrawn: (date: string) => void }) {
  const { t, lang } = useT();
  const { refresh } = useSession();
  const handle = useErrorHandler();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const state = passState(pass);
  const name = planById(pass.plan).name[lang];
  /* the refund date: from the server (refunded_at), or from this page's withdrawal just now */
  const refundedOn = withdrawnOn || (pass.status === 'refunded' ? pass.refunded_at || undefined : undefined);
  const withdrawable = canWithdraw(pass) && !refundedOn;

  useEffect(() => { if (confirming) confirmRef.current?.querySelector<HTMLElement>('h4')?.focus(); }, [confirming]);

  const withdraw = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ pass: Pass }>('/api/billing/withdraw', { pass_id: pass.id, confirm: true });
      onWithdrawn((r && r.pass && r.pass.refunded_at) || new Date().toISOString());
      setConfirming(false);
      void refresh();
    } catch (e) {
      /* 409 forbidden: the 14 days are over; the server says so in its message */
      if (e instanceof ApiError && e.code === 'forbidden') { setError(e.serverMessage || t('pass.withdrawOver')); setConfirming(false); void refresh(); }
      else { const msg = handle(e); if (msg) setError(msg); }
    } finally { setBusy(false); }
  };

  return (
    <div className="pass">
      <div className="pass__top">
        <span className="tile tile--orange"><Icon.pass /></span>
        <span className="row__main">
          <span className="row__title">{t('acct.pass', { plan: name })}</span>
          <span className="row__sub">
            {pass.valid_until && state !== 'refunded' && <span>{t('pass.validUntil', { date: formatDate(pass.valid_until, lang) })}</span>}
            <span>{t('pass.paidOn', { date: formatDate(pass.paid_at, lang) })}</span>
            {pass.paid_by === 'parent' && <span>{t('pass.byParent')}</span>}
          </span>
        </span>
        <Chip tone={refundedOn ? 'blue' : STATE_TONE[state]}>{t(refundedOn ? 'pass.refunded' : `pass.${state}`)}</Chip>
      </div>
      {refundedOn && <Alert tone="success">{t('pass.withdrawn', { date: formatDate(refundedOn, lang) })}</Alert>}
      {error && <Alert>{error}</Alert>}
      {withdrawable && !confirming && (
        <div className="pass__withdraw">
          <Button variant="danger" small onClick={() => setConfirming(true)}>{t('pass.withdraw')}</Button>
          <p className="hint">{t('pass.withdrawUntil', { date: formatDate(pass.refundable_until, lang) })}</p>
        </div>
      )}
      {withdrawable && confirming && (
        <div className="confirm" ref={confirmRef}>
          <h4 tabIndex={-1}>{t('pass.confirmTitle')}</h4>
          <ul className="ticks">
            <li>{t('pass.confirmRefund')}</li>
            <li>{t('pass.confirmAccess', { plan: name })}</li>
            <li>{t('pass.confirmReceipt')}</li>
          </ul>
          <div className="btnrow">
            <Button variant="danger-solid" small busy={busy} onClick={() => void withdraw()}>{t('pass.confirm')}</Button>
            <Button variant="gray" small onClick={() => setConfirming(false)}>{t('pass.keep')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
